import React, { useRef, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import SpeechToText from '../SpeechToText';

/**
 * ChatInput Component
 * Handles message input, speech-to-text, and message submission
 * 
 * @param {Object} props - Component props
 * @param {string} props.inputText - Current input text value
 * @param {Function} props.setInputText - Function to update input text
 * @param {Function} props.onSubmit - Function to handle form submission
 * @param {boolean} props.isLoading - Whether message is being processed
 * @param {boolean} props.initialLoading - Whether initial loading is in progress
 * @param {string} props.chatPhase - Current chat phase ('onboarding', 'main', 'loading', 'error')
 * @returns {JSX.Element} ChatInput component
 */
const ChatInput = ({
  inputText,
  setInputText,
  onSubmit,
  isLoading = false,
  initialLoading = false,
  chatPhase = 'main',
  autoplayEnabled = false,
  onAutoplayToggle,
  onOpenTalkMode,
  voiceModeEligible = false,
  onOpenVoiceMode,
  onVoiceInput,
  onListeningStart,
  onListeningEnd,
  onSpeechError
}) => {
  const inputRef = useRef(null);

  // Auto-focus input when loading states change
  useEffect(() => {
    if (!isLoading && !initialLoading) {
      inputRef.current?.focus();
    }
  }, [isLoading, initialLoading]);

  useEffect(() => {
    if (!initialLoading) {
      inputRef.current?.focus();
    }
  }, [initialLoading]);

  // Handle speech transcript
  const handleSpeechTranscript = useCallback((transcript) => {
    if (transcript.trim()) {
      setInputText((prevText) => {
        const newText = prevText
          ? `${prevText} ${transcript.trim()}`
          : transcript.trim();
        return newText;
      });

      // Mark that voice input was used for this message
      onVoiceInput?.();

      // Focus and position cursor at end
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
        }
      }, 100);
    }
  }, [setInputText, onVoiceInput]);

  // Handle form submission
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!inputText.trim() || isLoading || initialLoading || chatPhase === 'loading' || chatPhase === 'error') {
      return;
    }
    onSubmit(e);
  }, [inputText, isLoading, initialLoading, chatPhase, onSubmit]);

  // Handle keydown: Enter sends, Shift+Enter inserts newline
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  }, [handleSubmit]);

  const isDisabled = isLoading || initialLoading || chatPhase === 'loading' || chatPhase === 'error';

  const placeholder = chatPhase === 'onboarding' 
    ? "Respond to the onboarding assistant..." 
    : "Type your message...";

  return (
    <Box
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}
    >
      {/* Autoplay control row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: 1.5,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* Autoplay (Talk Mode) is now an ambient toggle in the persona header
            for cross-platform consistency, so it's no longer duplicated here. */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {voiceModeEligible && (
            <Tooltip title="Start voice conversation (Advanced Voice Mode)">
              <IconButton
                onClick={onOpenVoiceMode}
                color="secondary"
                disabled={isDisabled}
                size="small"
              >
                <HeadsetMicIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Open Talk Mode for audio conversation">
            <IconButton
              onClick={onOpenTalkMode}
              color="primary"
              disabled={isDisabled}
              size="small"
            >
              <RecordVoiceOverIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {/* Input row */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          p: 1.5
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={6}
          variant="outlined"
          size="small"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          helperText="Shift + Enter for new line"
          disabled={isDisabled}
          sx={{ mr: 1 }}
          FormHelperTextProps={{ sx: { mt: 0.25, fontSize: '0.7rem', opacity: 0.6 } }}
          data-testid="chat-message-input"
        />
        
        <SpeechToText
          onTranscript={handleSpeechTranscript}
          disabled={isDisabled}
          language="en-US"
          continuous={false}
          interimResults={true}
          showInterimText={false}
          onListeningStart={onListeningStart}
          onListeningEnd={onListeningEnd}
          onSpeechError={onSpeechError}
        />
        
        <Button
          type="submit"
          variant="contained"
          disabled={isDisabled}
          endIcon={<SendIcon />}
          data-testid="chat-send-button"
        >
          Send
        </Button>
      </Box>
    </Box>
  );
};

export default ChatInput;